import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';
import { fetchUserFavorites, setSelectedUserFavoritesSport } from 'stores/match/index.js';
import MatchCard from 'components/general/match_card.js';
import SportDetailItem from 'components/SportDetailItem.js';
import { groupedEventsBySport } from 'toolkit/eventsToGroup.js';
import { getSportTypeToActiveIcon, getSportTypeToWhiteIcon } from 'toolkit/utils';
import GoBack from 'components/general/go_back';
import { Col, Row } from 'react-bootstrap';
import { AWS_BUCKET_URL } from 'toolkit/constants';

function UserFavorites() {
  const dispatch = useDispatch();
  const selectedUserFavoritesSport = useSelector(
    (state) => state.match.selectedUserFavoritesSport
  );
  const userFavoritesEvents = useSelector(
    (state) => {
      const { userFavorites, events } = state.match;
      return userFavorites
        .map((c) => events[c])
        .filter((match) => {
          if (!match || !match.markets) return false;
          return (
            match.markets[0]?.outcomes?.[0]?.odds !== null ||
            match.markets[0]?.outcomes?.[1]?.odds !== null ||
            match.markets[0]?.outcomes?.[2]?.odds !== null
          );
        });
    },
    (a, b) => a.length === b.length
  );
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const { t } = useTranslation('translations', { useSuspense: false });
  const [sports, setSports] = useState({});
  const [sport, setSport] = useState('0');
  const location = useLocation();
  const [selectedMarketType, setSelectedMarketType] = useState(1);

  useEffect(() => {
    dispatch(fetchUserFavorites());
  }, []);

  useEffect(() => {
    const groups = groupedEventsBySport(userFavoritesEvents);
    setSports(groups);

    if ((sport === '' || Object.keys(sport).length === 0) && userFavoritesEvents.length > 0) {
      setSport(
        selectedUserFavoritesSport !== null ||
          sports.hasOwnProperty(Number.parseInt(selectedUserFavoritesSport)) === true
          ? selectedUserFavoritesSport
          : Object.keys(groups)[0]
      );
    }
  }, [userFavoritesEvents]);

  const selectSport = useCallback((id) => {
    setSport(id);
    dispatch(setSelectedUserFavoritesSport(id));
  });

  const calculateMenuItemsDynamic = () => {
    const menuItems = [];
    let icon = '';
    Object.keys(sports).map((sportType, index) => {
      if (sportType === sport) {
        icon = getSportTypeToActiveIcon(sport);
      } else {
        icon = getSportTypeToActiveIcon(sportType);
      }

      if (sports[sportType].length > 0) {
        menuItems.push(
          <SportDetailItem
            key={index}
            active={sport === sportType}
            count={_.sum(sports[sportType].map((l) => l.matches.length))}
            icon={icon}
            onClick={() => {
              selectSport(sportType);
            }}
            text={t('sports.' + sportType)}
            className={'d-flex flex-column p-2 fs-9'}
          />
        );
      }
    });
    return menuItems;
  };

  function getBetType() {
    const betTypes = [
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      },
      {
        label: t('doubleChance'),
        id: 10
      }
    ];
    return (
      <div className="tip-types">
        {betTypes.map((betType) => (
          <div className="d-inline-block w-25 fw-bold " key={betType.id}>
            <button
              className={`btn btn-sm w-100 border fs-8 ${selectedMarketType === betType.id ? 'btn-primary fw-bold text-white' : 'btn-light'
                }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function renderLeagueMatches(leagueGroup, matches, keyPrefix = '') {
    let code = null;
    if (matches && matches.length > 0 && matches[0].categoryId) {
      code = matches[0].categoryId.split(':')[2];
    }
    return (
      <Fragment key={keyPrefix + leagueGroup.league + matches[0].providerId}>
        <div className="text-start pb-2 fw-bold pt-1 fs-7 d-flex align-items-center">
          {code && (
            <img
              width="20"
              height="13"
              src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
              alt={code}
              className="me-2"
            />
          )}
          {leagueGroup.category + ' / ' + leagueGroup.league}
        </div>
        {matches.map((match, index) => (
          <MatchCard
            key={match.providerId}
            providerId={match.providerId}
            marketType={selectedMarketType}
            index={index}
            sport={sport}
            path={location.pathname}
            locales={locales}
            selectedLocale={selectedLocale}
          />
        ))}
      </Fragment>
    );
  }

  function getBets() {
    const matchCards = [];
    if (sports[sport] && sports[sport].length > 0) {
      const leagueGroups = sports[sport].filter((l) => l.matches.length > 0);

      // First pass: render favorite matches grouped by their leagues
      leagueGroups.forEach((leagueGroup) => {
        const favoriteMatches = leagueGroup.matches.filter((m) => m?.isFavorite === true);
        if (favoriteMatches.length > 0) {
          matchCards.push(renderLeagueMatches(leagueGroup, favoriteMatches, 'fav-'));
        }
      });

      // Second pass: render non-favorite matches grouped by their leagues
      leagueGroups.forEach((leagueGroup) => {
        const nonFavoriteMatches = leagueGroup.matches.filter((m) => m?.isFavorite !== true);
        if (nonFavoriteMatches.length > 0) {
          matchCards.push(renderLeagueMatches(leagueGroup, nonFavoriteMatches, 'reg-'));
        }
      });
    }

    return matchCards;
  }

  return (
    <div className="container-fluid scrollable-y">
      <div
        className="overflow-auto text-nowrap bg-dark mx-n3 d-flex align-items-center justify-content-between px-2"
        style={{ minHeight: '36px', paddingTop: '4px', paddingBottom: '4px' }}>
        <div
          style={{
            flexShrink: 0,
            padding: '2px 4px',
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'}>
          <div style={{ transform: 'scale(0.7)', filter: 'invert(1) brightness(2)' }}>
            <GoBack />
          </div>
        </div>
        <div className="d-flex flex-nowrap overflow-auto gap-0" style={{ flex: 1, minWidth: 0, marginLeft: '4px' }}>
          {calculateMenuItemsDynamic()}
        </div>
      </div>
      {sport !== '0' && <div style={{ height: '8px' }} />}
      {sport === '0' && (
        <Row className="">
          <Col className="tip-types-container">{getBetType()}</Col>
        </Row>
      )}
      <div className="mt-n2">
        <div>{getBets()}</div>
      </div>

      {/* No favorites message */}
      {userFavoritesEvents.length === 0 && (
        <div className="text-center py-5">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6c757d"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mb-3">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          <p className="text-muted">{t('noFavorites')}</p>
        </div>
      )}
    </div>
  );
}

export default UserFavorites;
